import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  ShieldAlert,
  AlertCircle,
  X,
  CheckCircle,
  Calendar,
} from 'lucide-react'
import api, { getAllCattle } from '../../services/api'
import PageHeader from '../../components/PageHeader'
import clsx from 'clsx'

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const getMastitisCases = (ranchId, params = {}) =>
  api.get('/dairy/mastitis-cases', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

const createMastitisCase = (data) =>
  api.post('/dairy/mastitis-cases', data).then(res => res.data)

const updateMastitisCase = (id, data) =>
  api.patch(`/dairy/mastitis-cases/${id}`, data).then(res => res.data)

const getDairyCattle = (ranchId) =>
  getAllCattle(ranchId, { status: 'active', category: 'cow' })

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const statusColors = {
  active: 'bg-red-100 text-red-800',
  treating: 'bg-amber-100 text-amber-800',
  resolved: 'bg-green-100 text-green-800',
  chronic: 'bg-purple-100 text-purple-800',
}

const statusLabels = {
  active: 'Active',
  treating: 'Treating',
  resolved: 'Resolved',
  chronic: 'Chronic',
}

const severityLabels = {
  subclinical: 'Subclinical',
  mild: 'Mild',
  moderate: 'Moderate',
  severe: 'Severe',
}

const severityColors = {
  subclinical: 'bg-blue-100 text-blue-800',
  mild: 'bg-yellow-100 text-yellow-800',
  moderate: 'bg-orange-100 text-orange-800',
  severe: 'bg-red-100 text-red-800',
}

const quarterOptions = [
  { value: 'LF', label: 'Left Front (LF)' },
  { value: 'RF', label: 'Right Front (RF)' },
  { value: 'LR', label: 'Left Rear (LR)' },
  { value: 'RR', label: 'Right Rear (RR)' },
  { value: 'multiple', label: 'Multiple Quarters' },
]

const detectionMethods = [
  { value: 'visual', label: 'Visual Inspection' },
  { value: 'cmt_test', label: 'CMT Test' },
  { value: 'lab_culture', label: 'Lab Culture' },
  { value: 'milk_conductivity', label: 'Milk Conductivity' },
]

const cmtScores = ['0', 'trace', '1', '2', '3']

// ---------------------------------------------------------------------------
// Add Case Modal
// ---------------------------------------------------------------------------

function AddCaseModal({ ranchId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    ranch_id: ranchId,
    animal_id: '',
    quarter: 'LF',
    severity: 'mild',
    detection_method: 'visual',
    cmt_score: '',
    detection_date: new Date().toISOString().split('T')[0],
    treatment_name: '',
    withhold_milk_days: '',
    notes: '',
  })
  const [error, setError] = useState('')

  const { data: cattleData } = useQuery({
    queryKey: ['dairyCattle', ranchId],
    queryFn: () => getDairyCattle(ranchId),
  })

  const mutation = useMutation({
    mutationFn: createMastitisCase,
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.detail || 'Failed to create mastitis case'),
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    if (!formData.animal_id) {
      setError('Please select a cow')
      return
    }
    const payload = {
      ...formData,
      cmt_score: formData.cmt_score || null,
      withhold_milk_days: formData.withhold_milk_days ? parseInt(formData.withhold_milk_days) : null,
      treatment_name: formData.treatment_name || null,
      notes: formData.notes || null,
    }
    mutation.mutate(payload)
  }

  const cows = cattleData || []

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Log Mastitis Case</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Cow */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cow *</label>
            <select
              required
              value={formData.animal_id}
              onChange={(e) => setFormData({ ...formData, animal_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">Select cow...</option>
              {cows.map((cow) => (
                <option key={cow.id} value={cow.id}>
                  {cow.local_name || cow.rfid_tag} ({cow.tag_id || cow.rfid_tag})
                </option>
              ))}
            </select>
          </div>

          {/* Detection Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Detection Date *</label>
            <input
              type="date"
              required
              value={formData.detection_date}
              onChange={(e) => setFormData({ ...formData, detection_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Quarter + Severity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quarter *</label>
              <select
                value={formData.quarter}
                onChange={(e) => setFormData({ ...formData, quarter: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {quarterOptions.map((q) => (
                  <option key={q.value} value={q.value}>{q.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity *</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="subclinical">Subclinical</option>
                <option value="mild">Mild</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
              </select>
            </div>
          </div>

          {/* Detection method + CMT */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Detection Method</label>
              <select
                value={formData.detection_method}
                onChange={(e) => setFormData({ ...formData, detection_method: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {detectionMethods.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CMT Score</label>
              <select
                value={formData.cmt_score}
                onChange={(e) => setFormData({ ...formData, cmt_score: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">Not tested</option>
                {cmtScores.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Treatment */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Treatment Name</label>
              <input
                type="text"
                value={formData.treatment_name}
                onChange={(e) => setFormData({ ...formData, treatment_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="e.g. Mastalone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Withhold Days</label>
              <input
                type="number"
                min="0"
                value={formData.withhold_milk_days}
                onChange={(e) => setFormData({ ...formData, withhold_milk_days: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Milk withhold days"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Optional observations..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              {mutation.isPending ? 'Saving...' : 'Log Case'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline Status Dropdown
// ---------------------------------------------------------------------------

function StatusDropdown({ caseItem, onUpdate }) {
  const [open, setOpen] = useState(false)
  const statuses = ['active', 'treating', 'resolved', 'chronic']

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className={clsx(
          'px-2 py-1 text-xs font-medium rounded-full cursor-pointer transition-colors',
          statusColors[caseItem.status] || 'bg-gray-100 text-gray-800'
        )}
      >
        {statusLabels[caseItem.status] || caseItem.status}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-32">
            {statuses
              .filter((s) => s !== caseItem.status)
              .map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    onUpdate(caseItem.id, { status: s })
                    setOpen(false)
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <span className={clsx(
                    'w-2 h-2 rounded-full',
                    {
                      active: 'bg-red-500',
                      treating: 'bg-amber-500',
                      resolved: 'bg-green-500',
                      chronic: 'bg-purple-500',
                    }[s]
                  )} />
                  {statusLabels[s]}
                </button>
              ))}
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function MastitisLog({ ranchId }) {
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [page, setPage] = useState(1)
  const limit = 20

  const { data, isLoading, error } = useQuery({
    queryKey: ['mastitisCases', ranchId, page],
    queryFn: () => getMastitisCases(ranchId, { limit, offset: (page - 1) * limit }),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateMastitisCase(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mastitisCases', ranchId] })
    },
  })

  const cases = data?.items || data || []
  const totalCount = data?.total || cases.length
  const totalPages = Math.ceil(totalCount / limit)

  const handleStatusUpdate = (id, payload) => {
    updateMutation.mutate({ id, payload })
  }

  const handleSuccess = () => {
    setShowAddModal(false)
    queryClient.invalidateQueries({ queryKey: ['mastitisCases', ranchId] })
  }

  if (error) {
    return (
      <div className="card p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Failed to load mastitis cases</h3>
        <p className="text-gray-500 mt-1">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mastitis Log"
        subtitle="Track udder health cases and treatment outcomes"
        icon={ShieldAlert}
        actions={
          <button
            onClick={() => setShowAddModal(true)}
            className="btn bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Case
          </button>
        }
      />

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : cases.length === 0 ? (
          <div className="text-center py-12">
            <ShieldAlert className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No mastitis cases recorded</h3>
            <p className="text-gray-500 mt-1">Keeping udders healthy -- add a case when one is detected</p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cow</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quarter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Treatment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cases.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {new Date(c.detection_date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {c.animal_name || c.cow_name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {c.quarter}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={clsx(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        severityColors[c.severity] || 'bg-gray-100 text-gray-800'
                      )}>
                        {severityLabels[c.severity] || c.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {c.treatment_name || '--'}
                      {c.withhold_milk_days ? (
                        <span className="text-xs text-gray-400 ml-1">({c.withhold_milk_days}d hold)</span>
                      ) : null}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusDropdown caseItem={c} onUpdate={handleStatusUpdate} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showAddModal && (
        <AddCaseModal
          ranchId={ranchId}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  )
}
